import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Server } from 'lucide-react';
import './Settings.css';

const TIME_OPTIONS = ['Ночь (01:00-06:00)', 'День (12:00-17:00)', 'Вечер (18:00-23:00)'];
const NETWORK_TYPES = ['WiFi', 'Мобильная'];

const PROVIDERS = {
  mts: { name: 'МТС', logo: '/images/providers/mts.png' },
  megafon: { name: 'Мегафон', logo: '/images/providers/megafon.png' },
  beeline: { name: 'Билайн', logo: '/images/providers/beeline.png' },
  rostelecom: { name: 'Ростелеком', logo: '/images/providers/rostelecom.png' },
  tele2: { name: 'Tele2', logo: '/images/providers/tele2.png' },
  vainah: { name: 'Вайнах', logo: '/images/providers/vainah.png' },
  wifi: { name: 'WiFi', logo: '/images/providers/wifi.png' }
};

function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [points, setPoints] = useState(0);
  const [settings, setSettings] = useState({
    provider: null,
    time_of_day: null,
    network_type: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Get user from WebApp
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      const userData = tg.initDataUnsafe?.user;
      if (userData) {
        setUser(userData);
      }
    }

    loadSettings();
    loadUserPoints();
  }, []);

  const loadUserPoints = async () => {
    try {
      const veraRes = await api.get('/vera/balance');
      if (veraRes.data) {
        setPoints(veraRes.data.total_points || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки очков:', error);
      setPoints(0);
    }
  };

  const loadSettings = async () => {
    try {
      const [settingsRes, userRes] = await Promise.all([
        api.get('/user/settings'),
        api.get('/user/me')
      ]);
      setSettings(settingsRes.data);
      setIsAdmin(userRes.data.is_admin || false);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (field, value) => {
    setSaving(true);
    try {
      const updated = { ...settings, [field]: value };
      await api.post('/user/settings', updated);
      setSettings(updated);
      window.Telegram?.WebApp?.showAlert('Настройки сохранены!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      window.Telegram?.WebApp?.showAlert('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleTelegramClick = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.HapticFeedback?.impactOccurred('light');
    }
    window.Telegram?.WebApp?.openTelegramLink('https://t.me/+wBXI4N49pwdhNDY1');
  };

  const handleClearCache = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        'Очистить кэш приложения? Это обновит интерфейс до последней версии.',
        (confirmed) => {
          if (confirmed) {
            // Очистка localStorage
            localStorage.clear();
            sessionStorage.clear();

            // Обновление страницы с force reload
            window.location.reload(true);
          }
        }
      );
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (confirm('Очистить кэш приложения?')) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload(true);
      }
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="settings">
      {/* Header with Points */}
      <div className="settings-header">
        <div className="points-display">
          <span className="points-icon">⭐</span>
          <span className="points-value">{points}</span>
          <span className="points-label">очков</span>
        </div>
        <div className="header-right">
          {user && (
            <div className="user-greeting">
              {user.first_name} 👋
            </div>
          )}
          <button className="telegram-chat-btn" onClick={handleTelegramClick} title="Присоединиться к чату">
            <svg className="telegram-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18.717-1.558 7.315-2.2 9.712-.271.996-.806 1.33-1.321 1.362-.563.035-1.175-.232-1.823-.58-1.011-.543-1.582-.88-2.563-1.41-1.133-.612-.399-1.013.247-1.601.169-.154 3.107-2.85 3.165-3.094.007-.031.014-.145-.054-.206s-.164-.04-.234-.024c-.1.024-1.688 1.073-4.764 3.149-.451.311-.86.462-1.226.454-.404-.009-1.18-.228-1.758-.416-.709-.23-1.272-.352-1.223-.743.025-.203.31-.41.855-.62 3.35-1.458 5.583-2.419 6.7-2.882 3.19-1.326 3.852-1.556 4.285-1.563.095-.002.307.022.445.134.116.094.148.221.163.31.015.088.034.29.019.447z"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="settings-header-title">
        <h2>⚙️ Настройки</h2>
        <p>Персонализируйте ваш опыт</p>
      </div>

      <div className="settings-section">
        <h3>Провайдер</h3>
        <div className="providers-grid">
          {Object.entries(PROVIDERS).map(([key, provider]) => {
            return (
              <button
                key={key}
                className={`provider-btn ${settings.provider === provider.name ? 'active' : ''}`}
                onClick={() => handleUpdate('provider', provider.name)}
                disabled={saving}
              >
                <div className="provider-logo-wrapper-small">
                  <img src={provider.logo} alt={provider.name} className="provider-logo-small" />
                </div>
                <span className="provider-name">{provider.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="settings-section">
        <h3>Время суток</h3>
        <div className="options-list">
          {TIME_OPTIONS.map((time) => (
            <button
              key={time}
              className={`option-btn ${settings.time_of_day === time ? 'active' : ''}`}
              onClick={() => handleUpdate('time_of_day', time)}
              disabled={saving}
            >
              {time}
            </button>
          ))}
        </div>
      </div>

      <div className="settings-section">
        <h3>Тип сети</h3>
        <div className="options-grid">
          {NETWORK_TYPES.map((network) => (
            <button
              key={network}
              className={`option-btn ${settings.network_type === network ? 'active' : ''}`}
              onClick={() => handleUpdate('network_type', network)}
              disabled={saving}
            >
              {network}
            </button>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="settings-section admin-section">
          <h3>Администрирование</h3>
          <button
            className="admin-servers-btn"
            onClick={() => navigate('/admin/servers')}
          >
            <Server size={20} strokeWidth={2} />
            Управление серверами
          </button>
          <p className="cache-hint">Добавление и управление защищенными серверами</p>
        </div>
      )}

      <div className="settings-section">
        <h3>Система</h3>
        <button
          className="clear-cache-btn"
          onClick={handleClearCache}
        >
          🔄 Очистить кэш и обновить
        </button>
        <p className="cache-hint">Используйте если интерфейс не обновляется</p>
      </div>

      <div className="settings-section">
        <h3>Telegram</h3>
        <button
          className="telegram-settings-btn"
          onClick={() => {
            if (window.Telegram?.WebApp) {
              window.Telegram.WebApp.HapticFeedback?.impactOccurred('light');
              window.Telegram.WebApp.openSettings();
            }
          }}
        >
          ⚙️ Настройки Mini App
        </button>
        <p className="cache-hint">Откроет настройки Telegram Mini App</p>
      </div>
    </div>
  );
}

export default Settings;
